import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-text-inverted hover:bg-accent-hover border-transparent',
  secondary: 'bg-surface text-text hover:bg-surface-raised border-border-strong',
  ghost: 'bg-transparent text-text-muted hover:bg-surface-raised border-transparent',
  danger: 'bg-danger-soft text-danger hover:bg-danger-soft border-transparent',
};

const SIZES: Record<Size, string> = {
  sm: 'text-sm px-3 py-1',
  md: 'text-sm px-4 py-2',
};

/** `forwardRef` porque diálogos precisam mover o foco para um botão concreto. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // `loading` também desabilita: um clique duplo enquanto a requisição
      // corre criaria uma segunda pesquisa (e um segundo custo).
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md border font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}
